import { createFileRoute, Link } from '@tanstack/react-router';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { Badge, type BadgeVariant } from '~/components/ui/Badge';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { PageTitle } from '~/components/ui/Page';
import {
  type NormalizedQuestion,
  QuestionAnswerView,
} from '~/components/ui/QuestionAnswerView';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';

type GradingSubmissionDetail = components['schemas']['GradingSubmissionDetail'];
type GradingQuestion = components['schemas']['GradingQuestion'];

export const Route = createFileRoute('/_admin/feedbacks/$submissionId')({
  component: FeedbackDetail,
});

function statusBadge(status: string): {
  label: string;
  variant: BadgeVariant;
} {
  switch (status) {
    case 'GRADED':
      return { label: 'Graded', variant: 'success' };
    case 'PENDING':
      return { label: 'Pending', variant: 'warning' };
    case 'FAILED':
      return { label: 'Failed', variant: 'error' };
    default:
      return { label: status, variant: 'neutral' };
  }
}

function toNormalized(q: GradingQuestion): NormalizedQuestion {
  const hasAnswer =
    q.user_answer !== null &&
    q.user_answer !== undefined &&
    !(Array.isArray(q.user_answer) && q.user_answer.length === 0) &&
    !(typeof q.user_answer === 'string' && q.user_answer === '');
  const isMultiple = q.type === 'choose_one' || q.type === 'choose_multiple';
  const choices = q.answers.map((a, i) => ({
    index: i,
    text: a.answer,
    is_correct: a.is_correct,
  }));
  // For multiple choice, derive boolean[] from is_chosen flags if user_answer
  // doesn't already supply it.
  let userAnswer: NormalizedQuestion['user_answer'] = q.user_answer ?? null;
  if (isMultiple && !Array.isArray(userAnswer)) {
    userAnswer = q.answers.map((a) => a.is_chosen);
  }
  return {
    question_id: q.id,
    type: q.type,
    text: q.content,
    choices,
    user_answer: userAnswer,
    has_answer: hasAnswer,
    is_correct: q.score > 0,
    feedback: q.feedback,
  };
}

function FeedbackDetail() {
  const { submissionId } = Route.useParams();
  const { data, isLoading, error } = api.adminGetFeedbackSubmission.useQuery({
    path: { submission_id: Number(submissionId) },
  });

  if (isLoading) {
    return <LoadingMessage>Loading feedback...</LoadingMessage>;
  }
  if (error) {
    return <ErrorMessage>Error loading feedback</ErrorMessage>;
  }
  if (!data) {
    return <ErrorMessage>Feedback submission not found</ErrorMessage>;
  }

  const { label, variant } = statusBadge(data.status);
  const detail = data as GradingSubmissionDetail;

  return (
    <Container>
      <BackLink to="/feedbacks">&larr; Back to Feedbacks</BackLink>

      <Header>
        <PageTitle>Feedback #{detail.id}</PageTitle>
        <Badge $variant={variant} $size="md">
          {label}
        </Badge>
      </Header>

      <Section>
        <SectionTitle>Overview</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>Student</FieldLabel>
            <FieldValue>{detail.student.display_name}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Study Helper</FieldLabel>
            <FieldValue>
              {detail.helper ? detail.helper.display_name : '—'}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Course</FieldLabel>
            <FieldValue>{detail.course.title}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Lesson</FieldLabel>
            <FieldValue>{detail.lesson.title}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Score</FieldLabel>
            <FieldValue>{detail.score}%</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Attempt</FieldLabel>
            <FieldValue>#{detail.attempt_number}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Submitted</FieldLabel>
            <FieldValue>
              {new Date(detail.created_at).toLocaleString()}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Graded</FieldLabel>
            <FieldValue>
              {detail.graded_at
                ? new Date(detail.graded_at).toLocaleString()
                : '—'}
            </FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      {detail.final_feedback ? (
        <Section>
          <SectionTitle>Final Thought</SectionTitle>
          <FinalFeedbackBlock>{detail.final_feedback}</FinalFeedbackBlock>
        </Section>
      ) : null}

      <Section>
        <SectionTitle>Questions, Answers & Feedback</SectionTitle>
        {detail.questions.length === 0 ? (
          <EmptyMessage>
            No questions recorded for this submission.
          </EmptyMessage>
        ) : (
          detail.questions.map((q, idx) => (
            <QuestionAnswerView
              key={q.id}
              question={toNormalized(q)}
              index={idx + 1}
            />
          ))
        )}
      </Section>
    </Container>
  );
}

const Container = styled.div``;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--blue);
  text-decoration: none;
  margin-bottom: 1rem;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const FinalFeedbackBlock = styled.div`
  white-space: pre-wrap;
  padding: 1rem;
  background: color-mix(in srgb, var(--blue) 8%, transparent);
  border-left: 3px solid var(--blue);
  border-radius: 4px;
`;

const EmptyMessage = styled.div`
  padding: 1rem;
  color: var(--grey-600);
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--grey-600);
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--error);
`;
